/**
 * Created: 2024-03-27
 * Last Updated: 2024-03-27
 *
 * Purpose: Base error page component for displaying all types of OAuth errors with flash messages
 *
 * Changes:
 * - Updated useFlashMessage import to use local context
 * - Fixed type usage for addMessage
 * - Fixed import path for flash message context
 * - Updated to use client imports utility for Chakra UI components
 */

'use client'

import React, {useEffect} from 'react'
import {useRouter} from 'next/navigation'
import {useFlashMessage} from '../../contexts/flash-message.context'
import {Box, Card, CardBody, VStack, Button, Alert, AlertIcon, AlertTitle, AlertDescription} from '@chakra-ui/react'

interface ErrorPageProps {
  title: string
  message: string
  error?: string
  redirectPath?: string
  redirectText?: string
  showHomeButton?: boolean
}

export const ErrorPage = ({title, message, error, redirectPath, redirectText = 'Try Again', showHomeButton = true}: ErrorPageProps) => {
  const router = useRouter()
  const {showError} = useFlashMessage()

  useEffect(() => {
    if (error) {
      showError(error)
    }
  }, [error, showError])

  const handleRedirect = () => {
    if (redirectPath) {
      router.push(redirectPath)
    } else {
      router.back()
    }
  }

  return (
    <Box maxW="md" mx="auto" mt={8}>
      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Alert status="error" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" borderRadius="md" py={6}>
              <AlertIcon boxSize={8} />
              <AlertTitle mt={4} mb={2} fontSize="lg">
                {title}
              </AlertTitle>
              <AlertDescription maxWidth="sm">{message}</AlertDescription>
            </Alert>

            <VStack spacing={3}>
              <Button colorScheme="primary" width="full" onClick={handleRedirect}>
                {redirectText}
              </Button>

              {showHomeButton && (
                <Button variant="ghost" width="full" onClick={() => router.push('/')}>
                  Return to Home
                </Button>
              )}
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
